import Debug from 'debug'
import { inject, injectable, multiInject } from 'inversify';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import {
  ObjectCompletionHandlerInterface,
  ObjectCompletionHandlerSymbol
} from '@/app/statistics/handlers/object-completion-handler-interface';
import {
  SectionProgressInterface,
  SectionCompletion,
  SectionInteraction
} from '@/app/statistics/section/section-progress';
import {
  createQuery,
  TrackingService2,
  TrackingService2Symbol,
  createCommand
} from '@/app/tracking/api'
import {
  SectionStatusChangedEvent,
  SectionStatusChangedEventName,
  calculateStatus
} from '@/app/statistics/events/section-status-events';
import {
  StatisticsUpdated,
  StatisticsUpdatedEventName
} from '@/app/statistics/events/section-interaction-events';
import { createEvent } from '@/app/event-stream';
import {
  allSectionIdsUnderPage,
  allSectionsUnderToc,
  isChapter,
  isChildSection,
  tocHrefEqualsPage
} from '@/app/aspect/toc-utils';
import { calculateSectionHeight } from './section-scroll-utils';
import { TocItem } from '@/app/shared/models';
import { getCurrentPage } from '@/app/shared/utils/route-utils';
import { maximumPossiblePageHeight } from '@/app/shared/utils/content-utils';
import { isQuiz } from '@/app/quiz/utils/quiz-utils';
//import { dispatchObjectCompletedEvent } from '../utils/object-completion-utils';
import { createInteractionId } from '@/app/statistics/section/section-interaction-utils';
//import { Result } from '@/app/tracking/tracking-api/result';
import sectionCompletionStoreModule from '../state/section-completion-state';
import sectionStoreModule from '../state/section-state';
import sectionInteractionStoreModule from '../state/section-interaction-state';
//import questionProgressStoreModule from '@/app/question/state/question-progress-state';
import aspectStoreModule from '@/app/aspect/apsect-state';
//import { QUESTION_TYPE } from '@/app/statistics/handlers/questions-completion-handler';
import * as SectionTrackingApi from '@/app/section-state/tracking/api'
//import * as InteractionTrackingApi from '@/app/interaction/tracking/api'
import { CompleteCourseCommandPayload, CompleteCourseCommand, UpdateCourseScoreCommand } from '@/app/course/tracking/api';
import { reportNonFatalError } from '@/app/error-handling';
import { getSectionParent } from './get-parent'
import { CourseCompletionService, CourseCompletionServiceSymbol } from '@/app/course-completion/service'

const INTERACTIVE_ELEMENTS_WEIGHT: number = .2;
const SECTION_COMPLETION_CALCULATION: SectionCompletionCalculationType = 'only-scroll'

const debug = Debug('section-progress-service')

type SectionCompletionCalculationType = 'interactions-and-scroll' | 'only-scroll'

@injectable()
export class SectionProgressService implements SectionProgressInterface {
  @inject(TrackingService2Symbol)
  private trackingService: TrackingService2

  @inject(CourseCompletionServiceSymbol)
  private courseCompletionService: CourseCompletionService

  @inject(EventStreamSymbol)
  private eventStream: EventStream;

  @multiInject(ObjectCompletionHandlerSymbol)
  private readonly handlerInstances: ObjectCompletionHandlerInterface[];

  // HACK: do not send course score always
  private currentCourseProgress = 0;

  public async findOrCreate(sectionId: string): Promise<SectionCompletion> {
    let completion = this.find(sectionId);
    if (completion === null || completion.toScroll === 0) {
      const progress = completion !== null ? completion.progress : null;
      completion = await this.create(sectionId, progress);

//      await this.queryAndSetInteractions(completion, completion.interactions.map((i) => i.id));
    }

    return completion;
  }

  public async load(sectionIds: string[]): Promise<void> {

    this.currentCourseProgress = 0

    debug('load count', sectionIds.length,
          'current count: ', Object.keys(sectionCompletionStoreModule.completionItemsBySectionId).length)

    sectionCompletionStoreModule.clear()

    let completions: SectionCompletion[] = [];
    let notFoundIds: string[] = [];
    for (const id of sectionIds) {
      const completion = this.find(id);
      if (completion !== null) {
        sectionCompletionStoreModule.setCompletion(completion);
        completions = [...completions, completion];
      } else {
        notFoundIds = [...notFoundIds, id];
      }
    }

    debug('count after load', Object.keys(sectionCompletionStoreModule.completionItemsBySectionId).length)

    const query = createQuery<SectionTrackingApi.QuerySectionStatesTrackingQuery>('query-section-states', {})
    const result = await this.trackingService.query(query)

    for (const notFoundId of notFoundIds) {
      const value: SectionTrackingApi.SectionState[] = result.sections;
      const foundTrackingResult: SectionTrackingApi.SectionState|undefined = value.find((v) => v.id === notFoundId);
      if (isChapter(notFoundId)) {
        continue
      }

      const trackingResult = foundTrackingResult || SectionTrackingApi.defaultSectionState(notFoundId)

      const progress = trackingResult.details.progress;
      const completion = await this.create(notFoundId, progress);
      sectionCompletionStoreModule.setCompletion(completion);
    }

    this.calculateTotalPercentAndSetToStore();
  }

  public find(sectionId: string): SectionCompletion|null {
    if (Object.keys(sectionCompletionStoreModule.completionItemsBySectionId).includes(sectionId)) {
      return sectionCompletionStoreModule.completionItemsBySectionId[sectionId];
    }

    return null;
  }

  public async saveInteraction(
    sectionId: string,
    interaction: any|null,
    scrolledPercent: number|null,
    page: number|null
  ): Promise<void> {
    let completion = await this.findOrCreate(sectionId);
    const previousProgress = { ...completion.progress };

    debug('saveInteraction', sectionId, interaction, scrolledPercent, page)

    // interaction
    if (interaction) {
      if (interaction.type && interaction.id) {
        interaction.id = this.createInteractionId(interaction.type, interaction.id);
      }

      this.iterateInstances((h: ObjectCompletionHandlerInterface) => {
        completion = h.setInteraction(completion, interaction);
      });
    }

    // scroll
    if (scrolledPercent !== null && completion.progress.scroll < scrolledPercent) {
      completion.progress.scroll = scrolledPercent;
    }

    // page
    if (page) {
      completion.progress.page++;
    }

    // total percent (completion)
    completion.progress.completion = this.calculatePercent(completion, SECTION_COMPLETION_CALCULATION);

    debug('section completion', sectionId, completion.progress.completion)

    const changed: boolean = completion.progress.completion > previousProgress.completion;
    if (changed) {
      debug('changed from', previousProgress.completion, completion.progress.completion)
      // set interaction states to store
      completion.interactions.forEach((i) => sectionInteractionStoreModule.setInteractionState({
        interactionId: i.id,
        completed: i.completed
      }));

      this.dispatchUpdatedEvent(completion);

      // set the new value to the store
      sectionCompletionStoreModule.setCompletion(completion);

      // if this section is a child section, we need to recalculate the parent's percent
      const parentId = getSectionParent(sectionId, aspectStoreModule.sectionsById)
      debug(`parent id of '${sectionId}' is '${parentId}'`)
      if (parentId) {
        // recalculate parent
        debug(`section '${sectionId}' progress changed, update parent's one: '${parentId}'`)
        await this.saveInteraction(parentId, {}, null, null);
      }
    } else {
      debug('no progress change', sectionId)
    }

    const title: string|null = aspectStoreModule.sectionsById[sectionId]
      ? aspectStoreModule.sectionsById[sectionId].title
      : sectionStoreModule.currentSectionTitle;
    if (changed && sectionId !== null && title !== null) {
      const event = createEvent<typeof SectionStatusChangedEventName, SectionStatusChangedEvent>(
        SectionStatusChangedEventName, {
          sectionId,
          title,
          progress: {
            completion: completion.progress.completion,
            scroll: completion.progress.scroll,
            page: completion.progress.page,
            status: calculateStatus(completion.progress.completion)
          },
          previousProgress: {
            ...previousProgress,
            status: calculateStatus(previousProgress.completion)
          }
        }
      );
      this.eventStream.dispatch(event);
    }

    this.calculateTotalPercentAndSetToStore();
  }

  public createInteractionId(type: string, id: string): string {
    return createInteractionId(type, id);
  }

  private async create(
    sectionId: string,
    progress: { completion: number; scroll: number; page: number; }|null = null
  ): Promise<SectionCompletion> {
    const sectionToc = aspectStoreModule.sectionsById[sectionId];
    const isChildResult = isChildSection(sectionId);

    let siblingSections: TocItem[];
    let pageCount: number = 0;
    if (isChildResult.isChild && isChildResult.parent !== null) {
      siblingSections = allSectionsUnderToc(isChildResult.parent);
    } else {
      siblingSections = allSectionsUnderToc(sectionToc);

      pageCount = $('.page.component').length - 1;
      if (pageCount < 0) {
        pageCount = 0;
      }
    }

    let toScroll: number = 0;
    let $container: any = null;
    const isSectionOnCurrentPage: boolean = sectionToc && tocHrefEqualsPage(getCurrentPage(), sectionToc.href);
    if (!isQuiz() && isSectionOnCurrentPage) {
      const isIntro: boolean = sectionToc.type === 'intro';
      toScroll = isIntro ? maximumPossiblePageHeight() : calculateSectionHeight(sectionId, siblingSections);
      $container = this.getContainer(sectionId, siblingSections);
    }

    let interactions: SectionInteraction[] = [];
    this.iterateInstances((handler: ObjectCompletionHandlerInterface) => {
      interactions = [...interactions, ...handler.calculateDefaultInteractions($container)];
    });

    this.setInteractionsMapToStore(interactions, sectionId);

    const maxScore = this.calculatePossibleMaxScore(toScroll, interactions.length, pageCount);

    const completion: SectionCompletion = {
      sectionId,
      toScroll,
      maxScore,
      progress: {
        completion: 0,
        scroll: 0,
        page: 0
      },
      interactions
    };

    if (progress === null) {
      const query = createQuery<SectionTrackingApi.QuerySectionStatesTrackingQuery>('query-section-states', {})
      const result = await this.trackingService.query(query)
      if (result.sections.length > 0) {
        const found = result.sections.find(x => x.id === sectionId)
        if (found) {
          progress = {
            ...found.details.progress
          }
        }
      }
    }

    if (progress !== null) {
      completion.progress = progress;
    }

    return completion;
  }

  // private async queryAndSetInteractions(completion: SectionCompletion, interactionIds: string[]): Promise<void> {
  //   const interactionCompletions = await this.queryInteractions(interactionIds);
  //   completion.interactions.forEach((interaction) => {
  //     if (interactionCompletions[interaction.id]) {
  //       interaction.completed = true;

  //       dispatchObjectCompletedEvent(interaction.id);
  //     }
  //   });

  //   completion.progress.completion = this.calculatePercent(completion, SECTION_COMPLETION_CALCULATION);

  //   sectionCompletionStoreModule.setCompletion(completion);

  //   this.dispatchUpdatedEvent(completion);
  // }

  private calculateTotalPercentAndSetToStore(): void {
    const completions = Object.values(sectionCompletionStoreModule.completionItemsBySectionId);
    debug('calculate-total-percent-and-set-to-store, completions length', completions.length)

    const total: number = completions.reduce((prev, current) => {

      if (isNaN(current.progress.completion)) {
        reportNonFatalError('NaN found in section completion, skipping in calculation', JSON.stringify(current), completions)
        return prev
      }

      return prev + current.progress.completion;
    }, 0);

    const totalPercent: number = completions.length > 0 ? Math.round(total / completions.length) : 0;
    debug('sum percent of sections', total, 'total percent', totalPercent)

    sectionCompletionStoreModule.setTotalPercent(totalPercent);

    this.processCourseCompletion(completions)
  }

  private processCourseCompletion(completions: SectionCompletion[]) {

    debug('process-section-start-percent, completions length', completions.length)

    // HACK: when user finished a section with minimum 90 percent and minimum 90% of all
    // of the section are completed, then complete the course.
    const completedSectionCount = completions.reduce((prev, current) => {
      if (current.progress.completion >= 90) {
        return prev + 1
      }
      return prev
    }, 0)

    const completedSectionCountPercent = completions.length > 0 ?
          completedSectionCount / completions.length : 0

    // debug('completed section percent', completedSectionCountPercent)
    // console.log('completed section percent', completedSectionCountPercent * 100)

    const progressToSend = Math.round(completedSectionCountPercent * 100)
    debug('completed section count', completedSectionCount, 'percent', completedSectionCountPercent, progressToSend)

    if (this.currentCourseProgress !== progressToSend) {
      this.currentCourseProgress = progressToSend
      this.courseCompletionService.reportSectionProgress(progressToSend)
    }
  }

  // private async queryInteractions(interactionIds: string[]): Promise<{ [interactionId: string]: boolean }> {
  //   const result: { [interactionId: string]: boolean } = {};

  //   let storedIds: string[] = Object.keys(sectionInteractionStoreModule.interactionStates);

  //   // set question completions
  //   questionProgressStoreModule.completedQuestionIds.forEach((id) => {
  //     const questionInteractionId: string = createInteractionId(QUESTION_TYPE, id);
  //     result[questionInteractionId] = true;

  //     storedIds = [...storedIds, questionInteractionId];
  //   });

  //   let notFoundIds: string[] = [];
  //   interactionIds.forEach((interactionId) => {
  //     if (storedIds.includes(interactionId)) {
  //       if (!result[interactionId]) {
  //         result[interactionId] = sectionInteractionStoreModule.interactionStates[interactionId];
  //       }
  //     } else {
  //       notFoundIds = [...notFoundIds, interactionId];
  //     }
  //   });

  //   if (notFoundIds.length === 0) {
  //     return result;
  //   }

  //   const query = createQuery<InteractionTrackingApi.QueryInteractionsTrackingQuery>('query-interactions',
  //                                                                                    { ids: notFoundIds})
  //   const interactionResult = await this.trackingService.query(query)

  //   // const queryResult: Result<InteractionState[], TrackingQueryError>
  //   //   = await this.trackingQuery.queryInteractionState(notFoundIds);
  //   // if (!queryResult.success) {
  //   //   return result;
  //   // }

  //   interactionResult.interactions.forEach((item) => {
  //     const passed: boolean = item.result === 'passed';
  //     const interactionId = item.id;
  //     result[interactionId] = passed;

  //     sectionInteractionStoreModule.setInteractionState({
  //       interactionId,
  //       completed: passed
  //     });
  //   });
  //   return result;
  // }

  private setInteractionsMapToStore(instanceInteractions: SectionInteraction[], sectionId: string): void {
    instanceInteractions.forEach((interaction) => {
      const interactionId: string = interaction.id;
      sectionInteractionStoreModule.setInteraction({ interactionId, sectionId });
    });
  }

  private iterateInstances(callback: (h: ObjectCompletionHandlerInterface) => void): void {
    this.handlerInstances.forEach((h) => callback(h));
  }

  private dispatchUpdatedEvent(completion: SectionCompletion): void {
    const event = createEvent<typeof StatisticsUpdatedEventName, StatisticsUpdated>(
      StatisticsUpdatedEventName, {
        completion
      }
    );
    this.eventStream.dispatch(event);
  }

  private calculatePercent(completion: SectionCompletion, type: SectionCompletionCalculationType): number {

    let percent
    switch (type) {
      case 'interactions-and-scroll': {
        const scrolled: number = completion.toScroll * (completion.progress.scroll / 100);
        let achieved: number = scrolled
              + (INTERACTIVE_ELEMENTS_WEIGHT * completion.toScroll * completion.progress.page);

        const toScrollMultiplier: number = completion.toScroll === 0 ? 1 : completion.toScroll;
        completion.interactions.forEach((interaction) => {
          if (interaction.completed) {
            achieved += INTERACTIVE_ELEMENTS_WEIGHT * toScrollMultiplier;
          }
        });

        percent = completion.maxScore === 0 ? 0 : Math.ceil(100 * achieved / completion.maxScore);

        break
      }
      case 'only-scroll': {
        percent = completion.progress.scroll
        break
      }
    }

    completion.progress.completion = percent;
    sectionCompletionStoreModule.setCompletion(completion);

    percent = this.calculateWithChildren(completion.sectionId, percent);
    debug('percent with children', completion.sectionId, percent)

    if (percent > 100) {
      return 100;
    }

    return percent;
  }

  private calculateWithChildren(sectionId: string, currentPercent: number): number {

    debug('calculateWithChildren', sectionId, currentPercent)

    const section: TocItem = aspectStoreModule.sectionsById[sectionId];
    if (!section) {
      debug('not found, return currentPercent', currentPercent)
      return currentPercent;
    }

    const selfPercent = currentPercent;
    const children = section.children || []
    const childIds = children.map(x => x.id)

    const isBlock = !section.href

    debug('child sections', childIds)

    if (section.href) {
      // we only use it for logging now
      const pageSectionIds = allSectionIdsUnderPage(section.href, section);
      debug('all sections under the page', pageSectionIds)

      // OBSOLOTE
      // for (const pageSectionId of pageSectionIds) {
      //   if (pageSectionId !== sectionId && childIds.indexOf(pageSectionId) < 0) {
      //     // it might missing because it might be a child of child, so we don't need to log here
      //     reportNonFatalError(`page section with id '${pageSectionId}' couldn't found among children`, pageSectionIds, childIds)
      //   }
      // }
    }

    if (childIds.length === 0) {
      return selfPercent
    }

    let childPercentSum = 0;
    for (const childId of childIds) {
      childPercentSum += sectionCompletionStoreModule.completionItemsBySectionId[childId].progress.completion
    }

    const childrenPercent = childIds.length > 0 ? childPercentSum / childIds.length : 0

    const SELF_WEIGHT = isBlock ? 0 : 0.2
    const weightedPercent = SELF_WEIGHT * selfPercent + (1 - SELF_WEIGHT) * childrenPercent

    debug('section id', sectionId,
          'is block', isBlock,
          'self percent', selfPercent,
          'children percent', childrenPercent,
          'weighted', weightedPercent)

    return weightedPercent
  }

  private calculatePossibleMaxScore(toScroll: number, interactionsCount: number, pageCount: number): number {
    const toScrollMultiplier: number = toScroll === 0 ? 1 : toScroll;
    return toScroll
      + (INTERACTIVE_ELEMENTS_WEIGHT * toScrollMultiplier * interactionsCount)
      + (INTERACTIVE_ELEMENTS_WEIGHT * toScrollMultiplier * pageCount);
  }

  private getContainer(sectionId: string, siblingSections: TocItem[]): any|null {
    const sectionIndex = siblingSections.findIndex((s) => s.id === sectionId);
    if (typeof sectionIndex === 'undefined') {
      return null;
    }

    const $sectionItem = $(`#${sectionId}`);

    const nextIndex: number = sectionIndex + 1;
    let $items;
    if (nextIndex < siblingSections.length) {
      const nextSection = siblingSections[nextIndex];
      $items = $sectionItem.nextUntil(`#${nextSection.id}`);
    } else {
      // last item, get elements till closing tag
      $items = $sectionItem.nextUntil();
    }

    return this.appendElementsToContainer($items, nextIndex, siblingSections);
  }

  // handling page component overlapping
  private appendElementsToContainer(
    $elements: any,
    nextIndex: number,
    siblingSections: TocItem[],
    $container: any|null = null
  ): any {
    if ($container === null) {
      $container = $('<div/>');
    }

    for (const element of $elements) {
      let $element = $(element);
      const isPageComponent: boolean = $element.hasClass('page') && $element.hasClass('component');
      if (isPageComponent) {
        $element = $element.children(':first');
        const $firstElement = $element.clone(true, true);
        $container.append($firstElement);
        let $pageChildren;
        if (nextIndex < siblingSections.length) {
          const nextSection = siblingSections[nextIndex];
          $pageChildren = $element.nextUntil(`#${nextSection.id}`);
        } else {
          // last item, get elements till closing tag
          $pageChildren = $element.nextUntil();
        }

        $container = this.appendElementsToContainer($pageChildren, nextIndex, siblingSections, $container);
      } else {
        const $newElement = $element.clone(true, true);
        $container.append($newElement);
      }
    }

    return $container;
  }
}
